import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { timeAgo } from '../lib/timeAgo';

/**
 * "App notifications" tab on the event editor (Evan 2026-11-02: "if they reject it i wont do text
 * blasts - instead I'll send updates through shado - so you should make a mini
 * announcements/notifications sender for me within the events page").
 *
 * Reuses the app's announcement pipeline end to end — nothing new server-side:
 *   send now  → admin_push_announcement_audience(p_only_user_ids = this event's guests)
 *               → notifications rows → send-push-notification → Expo push + in-app inbox.
 *   schedule  → scheduled_announcement_pushes row, replayed by run_due_announcement_pushes (cron,
 *               every minute), which already honours p_only_user_ids.
 * Every send carries campaign `event:<slug>`, which is how this tab finds its own history. A
 * "send now" also writes an already-'sent' row to scheduled_announcement_pushes so both kinds
 * show in one list (the runner only picks up 'scheduled' rows).
 */

const inputCls =
  'w-full rounded-lg border border-border bg-surface px-3 py-2 text-[13px] text-white placeholder:text-mutedDim focus:border-accent focus:outline-none';
const labelCls = 'mb-1 block text-[10.5px] uppercase tracking-[0.08em] text-mutedDim';

export type PushGuest = { name: string; user_id: string | null; group: 'list' | 'in' | 'pending' };

type Audience = 'all' | 'list' | 'checked_in' | 'not_checked_in' | 'pending' | 'custom';

const AUDIENCES: [Audience, string, string][] = [
  ['all', 'Everyone', 'Guest list + pending claims'],
  ['list', 'Guest list', 'Everyone with a ticket'],
  ['checked_in', 'Checked in', 'Already through the door'],
  ['not_checked_in', 'Not checked in', 'On the list, not here yet'],
  ['pending', 'Pending claims', 'Short of the ratings'],
  ['custom', 'Pick people', 'Choose exactly who'],
];

type HistoryRow = {
  id: number;
  run_at: string;
  params: { p_body?: string; p_title?: string; p_only_user_ids?: string[] };
  status: 'scheduled' | 'sending' | 'sent' | 'failed' | 'cancelled';
  result: string | null;
  sent_at: string | null;
};

function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function idsFor(a: Audience, guests: PushGuest[], picked: Set<string>): string[] {
  const keep = (g: PushGuest) =>
    a === 'all' ||
    (a === 'list' && g.group !== 'pending') ||
    (a === 'checked_in' && g.group === 'in') ||
    (a === 'not_checked_in' && g.group === 'list') ||
    (a === 'pending' && g.group === 'pending') ||
    (a === 'custom' && !!g.user_id && picked.has(g.user_id));
  return Array.from(new Set(guests.filter((g) => g.user_id && keep(g)).map((g) => g.user_id as string)));
}

export function EventPushTab({
  slug,
  eventTitle,
  guests,
}: {
  slug: string;
  eventTitle: string;
  guests: PushGuest[];
}) {
  const campaign = `event:${slug}`;
  const [audience, setAudience] = useState<Audience>('all');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [pickQuery, setPickQuery] = useState('');
  const [title, setTitle] = useState(eventTitle);
  const [body, setBody] = useState('');
  const [when, setWhen] = useState<'now' | 'later'>('now');
  const [sendAt, setSendAt] = useState(() => toLocalInput(new Date(Date.now() + 60 * 60 * 1000)));
  const [history, setHistory] = useState<HistoryRow[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // user_id → lock-screen push will reach them (events_v30 admin_push_status)
  const [pushOn, setPushOn] = useState<Map<string, boolean> | null>(null);
  const [showList, setShowList] = useState(false);
  const guestIdsKey = Array.from(new Set(guests.map((g) => g.user_id).filter(Boolean))).sort().join(',');

  useEffect(() => {
    const ids = guestIdsKey ? guestIdsKey.split(',') : [];
    if (!ids.length) return setPushOn(new Map());
    void supabase.rpc('admin_push_status', { p_user_ids: ids }).then(({ data, error }) => {
      if (error) return setPushOn(null);
      setPushOn(new Map(((data ?? []) as { user_id: string; push_on: boolean }[]).map((r) => [r.user_id, r.push_on])));
    });
  }, [guestIdsKey]);

  const loadHistory = useCallback(async () => {
    const { data, error } = await supabase
      .from('scheduled_announcement_pushes')
      .select('id, run_at, params, status, result, sent_at')
      .eq('params->>p_campaign', campaign)
      .order('run_at', { ascending: false })
      .limit(30);
    setHistory(error ? [] : ((data ?? []) as HistoryRow[]));
  }, [campaign]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    if (!history?.some((h) => h.status === 'scheduled' || h.status === 'sending')) return;
    const t = window.setInterval(() => void loadHistory(), 20_000);
    return () => window.clearInterval(t);
  }, [history, loadHistory]);

  const ids = idsFor(audience, guests, picked);
  const countFor = (a: Audience) => (a === 'custom' ? picked.size : idsFor(a, guests, picked).length);

  const submit = async () => {
    setNotice(null);
    const b = body.trim();
    const t = title.trim();
    if (b.length < 2) return setNotice('Write the message first.');
    if (ids.length === 0) return setNotice('Nobody in that group has a Shado account.');
    let runAt = new Date();
    if (when === 'later') {
      runAt = new Date(sendAt);
      if (Number.isNaN(runAt.getTime()) || runAt.getTime() < Date.now() + 60_000) {
        return setNotice('Pick a time at least a minute from now.');
      }
    }
    const who = AUDIENCES.find((a) => a[0] === audience)?.[1] ?? audience;
    const whenText =
      when === 'now'
        ? 'right now'
        : `at ${runAt.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`;
    if (
      !window.confirm(
        `Send a Shado notification to ${ids.length} ${ids.length === 1 ? 'person' : 'people'} (${who}) ${whenText}?\n\n${t || 'shado'}\n${b}`,
      )
    ) {
      return;
    }
    setBusy(true);
    const params: Record<string, unknown> = {
      p_body: b,
      p_campaign: campaign,
      p_only_user_ids: ids,
      ...(t ? { p_title: t } : {}),
    };
    if (when === 'now') {
      const { data, error } = await supabase.rpc('admin_push_announcement_audience', params);
      if (error) {
        setBusy(false);
        return setNotice(error.message);
      }
      const n = typeof data === 'number' ? data : ids.length;
      const now = new Date().toISOString();
      await supabase
        .from('scheduled_announcement_pushes')
        .insert({ run_at: now, params, status: 'sent', result: `${n} recipients`, sent_at: now });
      setNotice(`Sent to ${n} ${n === 1 ? 'person' : 'people'}.`);
    } else {
      const { error } = await supabase
        .from('scheduled_announcement_pushes')
        .insert({ run_at: runAt.toISOString(), params });
      if (error) {
        setBusy(false);
        return setNotice(error.message);
      }
      setNotice(`Scheduled — it goes out ${whenText}.`);
    }
    setBody('');
    setBusy(false);
    void loadHistory();
  };

  const cancel = async (h: HistoryRow) => {
    if (!window.confirm('Cancel this scheduled notification?')) return;
    const { data, error } = await supabase
      .from('scheduled_announcement_pushes')
      .update({ status: 'cancelled' })
      .eq('id', h.id)
      .eq('status', 'scheduled')
      .select('id');
    if (error) setNotice(error.message);
    else if (!data?.length) setNotice('Too late to cancel — it already started sending.');
    void loadHistory();
  };

  const withAccount = guests.filter((g) => g.user_id);
  const filtered = withAccount.filter((g) => g.name.toLowerCase().includes(pickQuery.trim().toLowerCase()));

  return (
    <>
      <div className="admin-card space-y-4">
        <div className="admin-eyebrow">New app notification</div>
        <p className="text-[12.5px] text-muted">
          Goes to guests through the Shado app: a lock-screen notification for anyone with notifications
          on, and a message in everyone&rsquo;s in-app notifications. Free, and no carrier approval needed.
        </p>

        <div>
          <label className={labelCls}>Who</label>
          <div className="flex flex-wrap gap-2">
            {AUDIENCES.map(([k, label, hint]) => (
              <button
                key={k}
                type="button"
                title={hint}
                onClick={() => setAudience(k)}
                className={`rounded-full border px-3 py-1.5 text-[12.5px] font-medium ${
                  audience === k
                    ? 'border-accent bg-accent/15 text-accent'
                    : 'border-border text-muted hover:text-white'
                }`}
              >
                {label} <span className="opacity-70">· {countFor(k)}</span>
              </button>
            ))}
          </div>
          {audience === 'custom' ? (
            <div className="mt-3 rounded-lg border border-border p-2">
              <input
                className={inputCls}
                placeholder="Search names"
                value={pickQuery}
                onChange={(e) => setPickQuery(e.target.value)}
              />
              <div className="mt-2 max-h-56 overflow-y-auto">
                {filtered.map((g) => {
                  const id = g.user_id as string;
                  return (
                    <label key={id} className="flex cursor-pointer items-center gap-2 px-1 py-1 text-[13px] text-white">
                      <input
                        type="checkbox"
                        checked={picked.has(id)}
                        onChange={() =>
                          setPicked((s) => {
                            const n = new Set(s);
                            if (n.has(id)) n.delete(id);
                            else n.add(id);
                            return n;
                          })
                        }
                      />
                      {g.name}
                      <span className="text-[11px] text-mutedDim">
                        {g.group === 'in' ? 'checked in' : g.group === 'pending' ? 'pending' : 'on list'}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>

        {/* Who in the chosen group gets a lock-screen push vs in-app only */}
        {pushOn ? (() => {
          const people = Array.from(
            new Map(guests.filter((g) => g.user_id && ids.includes(g.user_id)).map((g) => [g.user_id as string, g])).values(),
          );
          const on = people.filter((g) => pushOn.get(g.user_id as string));
          const off = people.filter((g) => !pushOn.get(g.user_id as string));
          return (
            <div className="rounded-lg border border-border px-3 py-2.5">
              <div className="flex flex-wrap items-center justify-between gap-2 text-[13px]">
                <span className="text-white">
                  <span className="font-semibold text-green-400">{on.length}</span> of {people.length} have notifications on
                  <span className="text-mutedDim">
                    {' '}· {off.length} will only see it inside the app
                  </span>
                </span>
                <button type="button" className="text-[12px] text-accent hover:underline" onClick={() => setShowList((v) => !v)}>
                  {showList ? 'Hide list' : 'Show who'}
                </button>
              </div>
              {showList ? (
                <div className="mt-3 grid gap-4 sm:grid-cols-2">
                  {(
                    [
                      ['Notifications on', on, 'text-green-400'],
                      ['Notifications off (in-app only)', off, 'text-mutedDim'],
                    ] as const
                  ).map(([label, list, cls]) => (
                    <div key={label}>
                      <div className={`mb-1 text-[10.5px] uppercase tracking-[0.08em] ${cls}`}>
                        {label} · {list.length}
                      </div>
                      <div className="max-h-56 space-y-0.5 overflow-y-auto text-[13px] text-white">
                        {list.length === 0 ? <div className="text-mutedDim">Nobody</div> : null}
                        {[...list]
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((g) => (
                            <div key={g.user_id}>{g.name}</div>
                          ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })() : null}

        <div>
          <label className={labelCls}>Title (first line)</label>
          <input
            className={inputCls}
            maxLength={60}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="shado"
          />
        </div>

        <div>
          <label className={labelCls}>Message</label>
          <textarea
            className={`${inputCls} min-h-[90px]`}
            maxLength={300}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Doors moved to 10pm — see you there."
          />
          <div className="mt-1 text-right text-[11px] text-mutedDim">{body.length}/300</div>
        </div>

        {/* Lock-screen preview */}
        <div className="max-w-sm rounded-2xl bg-white/10 px-4 py-3">
          <div className="text-[13px] font-semibold text-white">{title.trim() || 'shado'}</div>
          <div className="whitespace-pre-wrap text-[13px] text-white/85">{body.trim() || 'Your message'}</div>
        </div>

        <div>
          <label className={labelCls}>When</label>
          <div className="flex flex-wrap items-center gap-2">
            {(
              [
                ['now', 'Send now'],
                ['later', 'Schedule'],
              ] as const
            ).map(([k, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => setWhen(k)}
                className={`rounded-full border px-3 py-1.5 text-[12.5px] font-medium ${
                  when === k ? 'border-accent bg-accent/15 text-accent' : 'border-border text-muted hover:text-white'
                }`}
              >
                {label}
              </button>
            ))}
            {when === 'later' ? (
              <input
                type="datetime-local"
                className={`${inputCls} w-auto`}
                value={sendAt}
                onChange={(e) => setSendAt(e.target.value)}
              />
            ) : null}
          </div>
        </div>

        {notice ? <div className="text-[12.5px] text-accent">{notice}</div> : null}

        <button
          type="button"
          disabled={busy}
          onClick={() => void submit()}
          className="rounded-lg bg-accent px-4 py-2 text-[13px] font-semibold text-black disabled:opacity-50"
        >
          {busy ? 'Working…' : when === 'now' ? `Send to ${ids.length}` : `Schedule for ${ids.length}`}
        </button>
      </div>

      <div className="admin-card mt-6">
        <div className="admin-eyebrow mb-3">Sent &amp; scheduled</div>
        {history === null ? (
          <div className="text-[12.5px] text-mutedDim">Loading…</div>
        ) : history.length === 0 ? (
          <div className="text-[12.5px] text-mutedDim">Nothing sent yet.</div>
        ) : (
          <div className="space-y-3">
            {history.map((h) => (
              <div key={h.id} className="rounded-lg border border-border p-3">
                <div className="flex items-center justify-between gap-2 text-[11.5px] text-mutedDim">
                  <span>
                    <span
                      className={
                        h.status === 'sent'
                          ? 'text-green-400'
                          : h.status === 'failed'
                            ? 'text-red-400'
                            : h.status === 'cancelled'
                              ? 'text-mutedDim'
                              : 'text-yellow-300'
                      }
                    >
                      {h.status === 'scheduled' ? 'Scheduled' : h.status[0].toUpperCase() + h.status.slice(1)}
                    </span>
                    {' · '}
                    {h.status === 'scheduled'
                      ? new Date(h.run_at).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
                      : timeAgo(h.sent_at ?? h.run_at)}
                    {' · '}
                    {h.result ?? `${h.params.p_only_user_ids?.length ?? 0} people`}
                  </span>
                  {h.status === 'scheduled' ? (
                    <button type="button" className="text-red-400 hover:underline" onClick={() => void cancel(h)}>
                      Cancel
                    </button>
                  ) : null}
                </div>
                <div className="mt-1 text-[13px] font-semibold text-white">{h.params.p_title || 'shado'}</div>
                <div className="whitespace-pre-wrap text-[13px] text-white/85">{h.params.p_body}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
